import { useEffect, useRef, useState } from "react";
import MeasureTools from "./MeasureTools";
import { LAYERS as BUILTIN_LAYERS, insertMissingBuiltinLayers } from "./LayerControl";
import {
  CRS_WGS84,
  CRS_PL1992,
  DEFAULT_MAP_CRS,
  PL2000Crs,
  getProj4,
  pickPL2000ZoneByLongitude,
  registerProj4WithOpenLayers,
} from "@/src/map/projections";
import { DEFAULT_PROCEDURE_MAP_ZOOM, MAP_MAX_ZOOM, clampZoom, type LatLng } from "@/src/lib/procedureMapView";

export type ParcelInfo = {
  id: string;
  teryt: string;
  obreb: string;
  numer: string;
  voivodeship: string;
  district: string;
  commune: string;
};

/** Wybor ukladu wspolrzednych mapy dla tej instancji komponentu. */
export type MapCrsChoice = "PL2000" | "PL1992";

type OlMapProps = {
  latLng: LatLng;
  setLatLng: (ll: LatLng) => void;
  onParcelClick?: (info: ParcelInfo | null) => void;
  selectedParcelsGeometries?: Array<{ id: string; geometry: any; color?: string }>;
  /**
   * Pozycja warstwy wybranych działek (parcelsLayer) w stosie warstw:
   *  - false (default): NAD wszystkimi builtin layers (zIndex 8) — używane
   *    w mapie publicznej gdzie user-zaznaczone działki muszą być najbardziej
   *    widoczne (kontekst składania wniosku)
   *  - true: POD KIEG granicami działek (zIndex 3, między ortofotomapą
   *    a granicami) — używane w mapie wniosków (ApplicationsMapPage),
   *    żeby ortofotomapa była na dnie, wnioski powyżej, granice działek
   *    przykrywały wnioski w sposób czytelny.
   */
  selectedParcelsBelowCadastral?: boolean;
  /**
   * Układ widoku mapy. "PL2000" = auto-wybór strefy 2176/2177/2178/2179
   * na podstawie początkowej długości geograficznej; "PL1992" = EPSG:2180
   * (jeden układ dla całej PL). Default: "PL2000".
   */
  mapCrsChoice?: MapCrsChoice;
  /** Początkowy zoom mapy (0–22). Default: 10. */
  initialZoom?: number;
  /**
   * Kontekst Planu Ogólnego — gdy podany, mapa fetcha features z
   * `/tenant/{tenantId}/plan/{procedureId}/features` i renderuje je
   * jako warstwę wektorową ze stylami z `lib/planStyleBuilder.ts`
   * (kolory wg rozporządzenia 2023, etykiety oznaczeń, kreskowania
   * obszarów). Pozwala pokazać plan na głównej mapie wniosków, żeby
   * mieszkaniec widział strefy planistyczne wybierając działkę.
   */
  planContext?: { tenantId: string; procedureId: string };
  /**
   * Callback wywoływany po kliknięciu w warstwę plan. Przekazuje TABLICĘ
   * wszystkich feature'ów pod kliknięciem, posortowaną wg priorytetu
   * (strefa planistyczna → obszary specjalne → POG). Pusta tablica gdy
   * klik poza planem. Rodzic pokazuje pierwszy jako główny + resztę jako
   * dodatkowe informacje (PlanFeatureDetails renderuje oba poziomy).
   */
  onPlanFeatureClick?: (features: import("@/src/api/Plan").PlanFeatureProperties[]) => void;
  /**
   * ID feature'a (GeoJSON `id`) na który mapa ma zoom-ować. Używane
   * z PlanSearchBox — po wyborze strefy, mapa wykonuje `view.fit()`
   * na jej extencie. Zmiana wartości (nawet na tę samą) wyzwala re-fit.
   */
  planFocusFeatureId?: number | null;
  /**
   * Widoczność warstw. Gdy prop jest podany, rodzic zarządza widocznością
   * przez <LayerControl>. Gdy undefined, używamy defaultów (wsteczna
   * kompatybilność dla miejsc bez kontrolki, np. ApplicationsMapPage).
   */
  layerVisibility?: import("./LayerControl").LayerVisibilityState;
  /** Przezroczystość warstw plan/mpzp/studium (0..1). */
  layerOpacity?: import("./LayerControl").LayerOpacityState;
  /**
   * Niestandardowe warstwy WMS dodane przez usera (AddWmsDialog).
   * Każda: {id, url, layerName, opacity, visible}. OlMap tworzy po jednym
   * TileLayer/TileWMS per wpis. Kolejność w array = kolejność renderowania
   * (first element na dole, last na górze — standardowy OL).
   */
  customWmsLayers?: import("./AddWmsDialog").CustomWmsLayer[];
  /**
   * Label dla przycisku wyboru działki w popup po kliknięciu. Zależny od
   * typu procedury — 'Wskaż działkę do wniosku' albo '...do uwagi'.
   * Gdy undefined — popup wyboru akcji NIE pokazuje się (stary flow:
   * klik od razu zaznacza działkę + pokazuje popup planu jeśli jest).
   */
  parcelActionLabel?: string;
  /**
   * Druga akcja dla działki w popupie kliknięcia (np. „Nachylenie terenu
   * działki” na mapie konsultanta). Pobiera działkę z ULDK jak akcja główna,
   * ale wynik trafia do `onParcel` zamiast `onParcelClick`.
   */
  secondaryParcelAction?: { label: string; onParcel: (info: ParcelInfo) => void };
  /**
   * Klik obsługiwany od razu (`click` zamiast `singleclick`) i bez zoomu
   * dwuklikiem. `singleclick` czeka 250 ms na ewentualny drugi klik — szybkie
   * podwójne kliknięcie zamiast popupu akcji robiło zoom. Ustawiane przy
   * mouncie (mapa konsultanta). Default: false.
   */
  immediateClick?: boolean;
  /**
   * Klik w działkę już zaznaczoną (`selectedParcelsGeometries`) nadal woła
   * `onParcelClick`. Mapa konsultanta: działka wyszukana po numerze jest
   * podświetlona, a klik w nią ma otworzyć notatkę. Default: false
   * (mapa wniosków pomija duplikaty).
   */
  allowClickOnSelectedParcel?: boolean;
  /**
   * URL do pliku COG (Cloud Optimized GeoTIFF) — raster MPZP. Gdy podany,
   * mapa dodaje warstwę rastrową pod warstwą wektorową planu.
   */
  rasterCogUrls?: string[];
  /**
   * Callback wywoływany gdy instance ol/Map jest gotowy. Rodzic może
   * przechować referencję i przekazać do zewnętrznych komponentów (np.
   * MeasureTools osadzony w FloatingPanelu poza OlMap).
   */
  onMapReady?: (map: any) => void;
  /**
   * Ukryj wbudowany panel narzędzi pomiaru (MeasureTools). Default: false.
   * Ustaw na true, gdy pomiary są renderowane przez rodzica w innym miejscu.
   */
  hideMeasureTools?: boolean;
  /** Ukrywa podpowiedź „Kliknij na mapie, aby wybrać akcję” (np. geoankieta,
   *  gdzie kliknięcie dodaje punkt, a nie wybiera działkę). */
  hideClickHint?: boolean;
  /**
   * Kolejność warstw od dołu (index 0 = na dnie) do góry (ostatni = na
   * wierzchu). Items to LayerId dla built-in albo `custom:${id}` dla custom
   * WMS. Undefined = domyślna kolejność z init (`layers: [...]`).
   * Używane do setZIndex przy reorderze z LayerControl.
   */
  layerOrder?: string[];
};

/**
 * Wybór projekcji dla custom WMS na podstawie tego, co serwer zadeklarował
 * w GetCapabilities. Preferujemy EPSG:2180 (natywny widoku mapy — brak
 * reprojekcji), fallback na 3857 → 4326 (OL zrobi on-the-fly reprojekcję
 * kafelków do widoku). Gdy lista pusta lub serwer nie obsługuje żadnego
 * znanego CRS-u — użyjemy preferowanego (widoku), który serwer może
 * zwrócić 4xx i wtedy zobaczymy to w tileloaderror w konsoli.
 */
function pickWmsProjection(available: string[] | undefined, preferred: string): string {
  if (!available || available.length === 0) return preferred;
  const normalized = available.map((c) => c.toUpperCase());
  if (normalized.includes(preferred.toUpperCase())) return preferred;
  for (const fallback of ["EPSG:2180", "EPSG:3857", "EPSG:4326"]) {
    if (normalized.includes(fallback)) return fallback;
  }
  return available[0];
}

/**
 * GetCapabilities z cache localStorage (24 h) i timeoutem 8 s — dokument jest
 * statyczny, a serwery GUGIK okresowo odpowiadają 500 / pustą odpowiedzią.
 */
async function fetchCapabilitiesCached(url: string, cacheKey: string): Promise<string> {
  const TTL_MS = 24 * 60 * 60 * 1000;
  try {
    const raw = localStorage.getItem(cacheKey);
    if (raw) {
      const { xml, ts } = JSON.parse(raw);
      if (typeof xml === "string" && Date.now() - ts <= TTL_MS) return xml;
    }
  } catch { /* uszkodzony wpis — pobierz od nowa */ }
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 8000);
  try {
    const resp = await fetch(url, { signal: ctrl.signal });
    if (!resp.ok) throw new Error(`GetCapabilities HTTP ${resp.status}`);
    const xml = await resp.text();
    try {
      localStorage.setItem(cacheKey, JSON.stringify({ xml, ts: Date.now() }));
    } catch { /* quota exceeded — ignore */ }
    return xml;
  } finally {
    clearTimeout(timer);
  }
}

async function fetchParcelByPoint(lat: number, lng: number): Promise<ParcelInfo | null> {
  try {
    // ULDK Geoportalu oczekuje współrzędnych w PL-1992 (srid=2180),
    // dlatego konwertujemy z WGS84 przez centralnie zarejestrowany proj4.
    const proj4 = getProj4();
    const [x, y] = proj4(CRS_WGS84, CRS_PL1992, [lng, lat]);
    const url = `https://uldk.gugik.gov.pl/?request=GetParcelByXY&xy=${x},${y}&result=teryt,voivodeship,county,commune,region,parcel&srid=2180`;
    // Timeout — zawieszone żądanie ULDK blokowało kolejne kliknięcia
    // (parcelLoadingRef) bez końca.
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 15000);
    const text = await fetch(url, { signal: ctrl.signal })
      .then((r) => r.text())
      .finally(() => clearTimeout(timer));
    const lines = text.split("\n");
    if (lines[0]?.includes("-1") || lines.length < 2) return null;
    const data = lines[1]?.split("|");
    if (!data || data.length < 6) return null;
    const teryt = data[0] || "";
    const parts = teryt.split(".");
    const communePart = parts[0]?.replace("_", "") || "";
    return {
      id: teryt, teryt: communePart, obreb: data[3] || "",
      numer: data[5] || "", voivodeship: data[1] || "",
      district: data[2] || "", commune: data[3] || "",
    };
  } catch (err) {
    console.error("Failed to fetch parcel info:", err);
    return null;
  }
}

function OlMap({ latLng, setLatLng, onParcelClick, selectedParcelsGeometries, selectedParcelsBelowCadastral = false, mapCrsChoice = "PL1992", initialZoom = DEFAULT_PROCEDURE_MAP_ZOOM, planContext, onPlanFeatureClick, planFocusFeatureId, layerVisibility, layerOpacity, customWmsLayers, parcelActionLabel, secondaryParcelAction, immediateClick = false, allowClickOnSelectedParcel = false, rasterCogUrls, onMapReady, hideMeasureTools, hideClickHint, layerOrder }: OlMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<any>(null);
  const markerSourceRef = useRef<any>(null);
  const parcelsSourceRef = useRef<any>(null);
  // Plan Ogólny — opcjonalna warstwa wektorowa renderowana gdy `planContext`
  // jest podane. Source trzymamy w refie żeby useEffect mógł re-fetchnąć
  // gdy się zmieni procedureId. Layer trzymamy żeby click handler mógł
  // filtrować po nim (forEachFeatureAtPixel layerFilter).
  const planSourceRef = useRef<any>(null);
  const planLayerRef = useRef<any>(null);
  // Custom WMS layers added by user — trzymamy Map<id, TileLayer> żeby
  // móc diff-ować przy zmianie props (dodaj/usuń/zmień opacity/visible).
  const customWmsLayerRefs = useRef<Map<string, any>>(new Map());
  // Ref dla opcji pokazywania etykiet obszarów specjalnych (POG/OUZ/OZS/OSD).
  // Style callback z planLayer używa tego ref — zmiana przez useEffect
  // + `planLayer.changed()` wymusza rerender, bez re-mount warstwy.
  const showAreaLabelsRef = useRef(false);
  // Ref dla widoczności samych obszarów specjalnych (niezależnie od etykiet).
  // Style callback zwraca pusty Style dla obszarów gdy false.
  const areasVisibleRef = useRef(true);
  // Refy do wszystkich warstw — do togglowania widoczności z propsa
  // `layerVisibility` (useEffect nasłuchujący).
  const layerRefs = useRef<{
    osm?: any;
    ortho?: any;
    cieniowanie?: any;
    mpzp?: any;
    studium?: any;
    dzialki?: any;
    plan?: any;
    budynki?: any;
    raster?: any;
  }>({});
  // Callback przez ref (singleclick handler rejestrowany raz przy mount,
  // a callback z propsa może się zmieniać między renderami).
  const onPlanFeatureClickRef = useRef(onPlanFeatureClick);
  useEffect(() => { onPlanFeatureClickRef.current = onPlanFeatureClick; }, [onPlanFeatureClick]);
  // Ref dla parcelActionLabel — w handlerze kliknięcia decyduje czy pokazać
  // popup wyboru (Tryb A) czy od razu wykonać akcję (Tryb B).
  const parcelActionLabelRef = useRef(parcelActionLabel);
  useEffect(() => { parcelActionLabelRef.current = parcelActionLabel; }, [parcelActionLabel]);
  const secondaryParcelActionRef = useRef(secondaryParcelAction);
  useEffect(() => { secondaryParcelActionRef.current = secondaryParcelAction; }, [secondaryParcelAction]);
  // CRS widoku mapy — wybrany raz przy mouncie. Dla PL2000: strefa wybierana po
  // długości geograficznej początkowej pozycji. Dla PL1992: zawsze EPSG:2180.
  // OpenLayers nie pozwala zmienić projection widoku w locie, więc zmiana wymaga
  // re-mount mapy (np. przeładowanie strony po zmianie ustawień w panelu admina).
  const mapCrsRef = useRef<PL2000Crs | typeof CRS_PL1992>(DEFAULT_MAP_CRS);
  const [ready, setReady] = useState(false);
  // Współrzędne kursora — wyświetlane w pasku statusu pod mapą. Aktualizowane
  // przy pointermove. Pomaga użytkownikowi widzieć, że mapa jest interaktywna,
  // nawet gdy kafle GUGIK ładują się wolno.
  //
  // Wyświetlamy współrzędne w tym samym układzie, w którym wyświetlana jest mapa
  // (PL-1992 EPSG:2180 lub strefa PL-2000 EPSG:2176..2179) — wartości w metrach,
  // bo tak są zdefiniowane oba układy kartezjańskie. Dla geodety/urzędnika jest
  // to odczyt „natywny"; WGS84 byłby mniej użyteczny w kontekście planowania
  // przestrzennego (plany/decyzje operują na 2180 lub strefach PL-2000).
  const [cursorXY, setCursorXY] = useState<{ x: number; y: number; crs: string } | null>(null);
  // Licznik ładujących się kafli — gdy > 0, pokazujemy „Ładowanie kafli…"
  // żeby user wiedział, że mapa pracuje, a nie się zawiesiła.
  const [tilesLoading, setTilesLoading] = useState(0);
  // Czy trwa fetch ULDK po kliknięciu w działkę. Sterowane przez handler
  // `singleclick` w OlMap. Daje:
  //  - wizualny feedback (cursor: wait + tekst w pasku statusu),
  //  - blokadę re-clickowania (drugi click podczas pendingu jest ignorowany,
  //    bo race condition: oba fetche dostają to samo info, oba próbują dodać
  //    do listy zanim pierwsze setSelectedParcels się zacommituje).
  const [parcelLoading, setParcelLoading] = useState(false);
  // ULDK nie zwrócił działki dla klikniętego punktu (brak działki albo błąd
  // sieci) — krótki komunikat zamiast cichego braku reakcji.
  const [parcelMiss, setParcelMiss] = useState(false);
  // Refs używane wewnątrz async callbacku `singleclick`, żeby nie zamykać się
  // po stale state (closure'a w OL event listener).
  const parcelLoadingRef = useRef(false);
  const selectedParcelsRef = useRef<Array<{ id: string }>>([]);
  // Czy widok był już „dotknięty” — przez użytkownika (przeciągnięcie, kółko)
  // albo przez dopasowanie do wybranej działki. Dopóki nie, wolno nam
  // automatycznie dopasować widok do zasięgu rastra planu po jego wczytaniu;
  // potem już nie — inaczej raster (ładowany chwilę po mapie) odbierał
  // użytkownikowi właśnie wybraną/wyszukaną działkę i pokazywał całą gminę.
  const viewTouchedRef = useRef(false);
  // ID działek z poprzedniego renderu — żeby dopasować widok tylko do NOWO
  // dodanej działki (wyszukanej po numerze / klikniętej), nie przy usuwaniu
  // ani przy hurtowym wczytaniu listy (mapa wniosków).
  const prevParcelIdsRef = useRef<Set<string> | null>(null);

  // Popup wyboru akcji przy kliknięciu — "Pokaż plan" vs "Wskaż działkę".
  // Wartość ustawiana w singleclick handlerze, czyszczona gdy user wybierze
  // opcję lub ruszy mapę (movestart).
  const [pendingChoice, setPendingChoice] = useState<{
    pixel: [number, number];
    coordinate: [number, number];
    planHits: import("@/src/api/Plan").PlanFeatureProperties[];
  } | null>(null);
  // Funkcja zamykająca "wybierz działkę" — ustawiana w useEffect init mapy,
  // wywoływana z popupu (po React-owym wyborze użytkownika).
  const performParcelClickRef = useRef<
    ((coord: [number, number], onInfo?: (info: ParcelInfo) => void) => void | Promise<void>) | null
  >(null);

  // Initialize map with dynamic imports to avoid ESM issues
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;

    let cancelled = false;

    (async () => {
      // Najpierw rejestracja definicji proj4 (2180 + 2176–2179) oraz `register(proj4)`
      // po stronie OpenLayers — zrobione centralnie, żeby nie powielać tego w każdym pliku.
      await registerProj4WithOpenLayers();

      // Wybór CRS widoku mapy. Zamrażamy na czas życia instancji (OL nie pozwala
      // zmienić projection widoku w locie). Default to PL-1992 (EPSG:2180) —
      // natywny matrixSet WMTS GUGIK, brak reprojekcji. Tryb PL-2000 zostaje
      // jako opcja (auto-wybor strefy po dlugosci geograficznej), ale wymaga
      // reprojekcji EPSG:3857 → strefa po stronie OL i nie jest aktualnie
      // wykorzystywany przez `TenantPublicPage`.
      const mapCrs = mapCrsChoice === "PL2000"
        ? pickPL2000ZoneByLongitude(latLng.lng)
        : CRS_PL1992;
      mapCrsRef.current = mapCrs;

      // Dynamic imports - avoids top-level ESM initialization conflicts
      const [
        { default: OlMap },
        { default: OlView },
        { default: TileLayer },
        { default: TileWMS },
        { default: WMTS, optionsFromCapabilities },
        // WMTSTileGrid: nieużywany bezpośrednio — `optionsFromCapabilities`
        // tworzy własny tileGrid z dokumentu Capabilities GUGIK.
        { default: WMTSCapabilities },
        { default: ImageLayer },
        { default: ImageWMS },
        { default: VectorLayer },
        { default: VectorSource },
        { Feature },
        { Point },
        { Style, Fill, Stroke, Circle: CircleStyle },
        { fromLonLat, toLonLat },
      ] = await Promise.all([
        import("ol/Map"),
        import("ol/View"),
        import("ol/layer/Tile"),
        import("ol/source/TileWMS"),
        import("ol/source/WMTS"),
        import("ol/format/WMTSCapabilities"),
        import("ol/layer/Image"),
        import("ol/source/ImageWMS"),
        import("ol/layer/Vector"),
        import("ol/source/Vector"),
        import("ol"),
        import("ol/geom"),
        import("ol/style"),
        import("ol/proj"),
      ]);

      if (cancelled) return;

      // Import CSS
      await import("ol/ol.css");

      const markerSource = new VectorSource();
      const parcelsSource = new VectorSource();
      markerSourceRef.current = markerSource;
      parcelsSourceRef.current = parcelsSource;

      const markerStyle = new Style({
        image: new CircleStyle({
          radius: 8,
          fill: new Fill({ color: "#3b82f6" }),
          stroke: new Stroke({ color: "#fff", width: 2 }),
        }),
      });

      // Wyszukane/wybrane dzialki: tylko obwodka, bez wypelnienia —
      // nie zaslania ortofotomapy ani planu pod spodem.
      const selectedStyle = new Style({
        stroke: new Stroke({ color: "#00ff00", width: 3 }),
      });

      // Throttle ładowania kafli WMS.
      // Cel: nie przeciążać serwerów WMS (szczególnie lokalnych z limitem 429, jak
      // wms.nowosadecki.pl/iip/ows), utrzymywać < ~5 req/s na źródło, ładować od
      // środka mapy. OL domyślnie priorytetyzuje kafle bliżej centrum widoku (przez
      // TileQueue), ale nie limituje współbieżności — dlatego dodajemy własny limiter.
      //
      // Każde źródło WMS dostaje osobny limiter, więc limit na GUGIK nie blokuje
      // limitu na lokalny WMS gminy (i odwrotnie).
      function createThrottledTileLoader(maxConcurrent: number, minGapMs: number) {
        let active = 0;
        let lastStart = 0;
        const pending: Array<() => void> = [];

        const pump = () => {
          if (!pending.length || active >= maxConcurrent) return;
          const now = Date.now();
          const wait = Math.max(0, minGapMs - (now - lastStart));
          if (wait > 0) {
            setTimeout(pump, wait);
            return;
          }
          const task = pending.shift();
          if (!task) return;
          active++;
          lastStart = Date.now();
          task();
          // spróbuj odpalić kolejne od razu, jeśli nie wyczerpany limit współbieżności
          pump();
        };

        return function tileLoadFunction(tile: any, src: string) {
          const img: HTMLImageElement = tile.getImage();
          const done = () => {
            active = Math.max(0, active - 1);
            pump();
          };
          pending.push(() => {
            img.addEventListener("load", done, { once: true });
            img.addEventListener("error", done, { once: true });
            img.src = src;
          });
          pump();
        };
      }

      // Loader z limitem uzywany juz TYLKO przez WMS fallback ortofoto
      // (4 rownolegle / 250 ms = ~4 req/s): WMS GetMap dynamicznie renderuje
      // kafle z GeoTIFF (kosztowne po stronie GUGIK), wiec niskie limity,
      // zeby nie zadlawic serwera, gdy WMTS GetCapabilities padnie.
      //
      // HISTORIA: wczesniej byl tez `parcelsTileLoader` (5 req/s) dla kafli
      // 256 px WMS KIEG (dzialki + budynki + numery). KIEG to router do
      // powiatowych serwerow WMS (np. pow. nowosadecki), ktore przy >5 req/s
      // odpowiadaly 429 — a jeden widok mapy to 30+ kafli, czyli 6+ s
      // ladowania granic dzialek. Teraz dzialki, budynki i numery ida jako
      // JEDEN obraz WMS na widok (ImageWMS, jak "single tile" / externalAccess
      // w Lizmap) — 1 zadanie zamiast 30, limit 429 nie gra roli.
      //
      // KOLEJNOSC ZADAN: OL TileQueue priorytetuje kafle po dystansie od centrum
      // viewportu (`getTilePriority`). My uzywamy `pending.push`/`pending.shift`
      // (FIFO) — zachowujemy kolejnosc OL, wiec kafle centralne ida pierwsze.
      const orthoTileLoader = createThrottledTileLoader(4, 250);

      // UWAGA dot. protokołu: WMS 1.3.0 wymaga BBOX w kolejności osi zgodnej z definicją
      // EPSG (dla 2176–2179: northing, easting). OpenLayers wysyła east-north, przez co
      // GUGIK otrzymuje BBOX, którego nie potrafi zmapować i zamyka połączenie bez
      // odpowiedzi (net::ERR_EMPTY_RESPONSE, nie ServiceException XML).
      // Rozwiązanie: WMS 1.1.1 używa SRS= zamiast CRS= i zawsze BBOX w east-north
      // niezależnie od CRS, więc problem osi nie istnieje. GUGIK wspiera 1.1.1 natywnie
      // w PL-2000 — dzięki temu żądania są w rodzimym układzie mapy, bez reprojekcji.
      const WMS_VERSION = "1.1.1";

      // Ortofotomapa: zawsze próbujemy WMTS (kafle są wstępnie cięte i cache'owane
      // po stronie GUGIK — TTFB rzędu 100–300 ms). WMS GetMap renderuje każdy
      // kafelek dynamicznie z GeoTIFF i potrafi trzymać połączenie otwarte
      // 90–400 s przed pierwszym bajtem — stąd odczuwalne zamulenie, jeśli
      // padniemy do fallbacku.
      //
      // GUGIK WMTS publikuje matrixSet `EPSG:2180` (PL-1992). W widoku
      // natywnie 2180 nie ma reprojekcji (szybko, ostro).
      //
      // UWAGA: `preload` zostaje na 0. Testy na prod (2026-04-13) pokazaly,
      // ze `preload: 2` w polaczeniu z throttle (4 rownolegle, 250 ms gap)
      // powoduje kumulacje 1000+ pending tile requests (OL wsadza do kolejki
      // kafle dla 2 poziomow zoomu w dol + bufor bokow, throttle zwalnia
      // po ~4/s, viewport sie rusza i dorzuca nowe — kolejka nigdy sie nie
      // oproznia). Efekt: mapa renderowana jako biala mimo udanych GetTile
      // 200 OK. Bezpieczniejsze jest preload=0 + cache kafli w Caddy
      // reverse-proxy jako dalszy krok optymalizacji.
      //
      // Fallback WMS 1.1.1 zostaje na wypadek gdyby GetCapabilities padlo
      // (blad sieci / CORS / parsowanie XML).
      let orthoLayer: any = null;

      const buildOrthoWmsFallback = () =>
        new TileLayer({
          // preload: 1 = pre-fetch kafle 1 zoom level wyżej i niżej zanim
          // user zoom-uje. WMS fallback dla orto, throttle 4req/s spowalnia
          // ten preload, ale subiektywnie pan/zoom feels smoother.
          preload: 1,
          source: new TileWMS({
            url: "https://mapy.geoportal.gov.pl/wss/service/PZGIK/ORTO/WMS/StandardResolution",
            params: { LAYERS: "Raster", FORMAT: "image/jpeg", VERSION: WMS_VERSION },
            projection: mapCrs,
            // BEZ `crossOrigin` — mapy.geoportal.gov.pl nie zwraca
            // `Access-Control-Allow-Origin` na kaflach GetMap/GetTile.
            // Z `crossOrigin: "anonymous"` <img> pada na CORS error.
            // Bez tego atrybutu OL uzywa `<img>` w DOM (nie canvas),
            // taint canvas nie jest problemem bo nie czytamy pikseli.
            tileLoadFunction: orthoTileLoader,
          }),
        });

      try {
        // GUGIK GetCapabilities — idzie przez reverse-proxy Caddy.
        //
        // Serwer `mapy.geoportal.gov.pl` (WMTS ORTO) NIE zwraca naglowka
        // `Access-Control-Allow-Origin` dla zadan cross-site. Browser
        // przerywa polaczenie — Chrome raportuje `net::ERR_EMPTY_RESPONSE`
        // zamiast typowego komunikatu CORS, ale to ten sam problem
        // (serwer aktywnie zamyka TCP przy preflight/cors request).
        //
        // Rozwiazanie: frontend odpytuje sciezke wzgledna na naszej
        // domenie, Caddy forwarduje do GUGIK jako serwer-side fetch
        // (brak pojecia origin). Relatywny URL = same-origin = zero
        // CORS. Przy okazji Caddy moze cache'owac XML (TTL 1h).
        //
        // Wymagana konfiguracja w Caddyfile (bloku wniosekdoplanu.pl,
        // PRZED `handle { reverse_proxy wniosekmpzp2026-frontend-1:80 }`):
        //
        //   handle_path /proxy/gugik-wmts-caps {
        //       rewrite * /wss/service/PZGIK/ORTO/WMTS/StandardResolution?service=WMTS&request=getCapabilities
        //       reverse_proxy https://mapy.geoportal.gov.pl {
        //           header_up Host mapy.geoportal.gov.pl
        //       }
        //       header Cache-Control "public, max-age=3600"
        //   }
        //
        // Query string idzie w URL-u frontendu — dzieki temu Caddy
        // musi tylko przemapowac sciezke (`/proxy/gugik-wmts-caps` →
        // `/wss/service/PZGIK/ORTO/WMTS/StandardResolution`) a query
        // przekazuje bez modyfikacji. `&` w rewrite-argumencie Caddyfile
        // bywa zle parsowany (traktowany jako separator dyrektyw).
        //
        // Wymagana konfiguracja w Caddyfile (bloku wniosekdoplanu.pl,
        // PRZED ostatnim `handle { reverse_proxy ...frontend-1:80 }`):
        //
        //   handle /proxy/gugik-wmts-caps {
        //       uri replace /proxy/gugik-wmts-caps /wss/service/PZGIK/ORTO/WMTS/StandardResolution
        //       reverse_proxy https://mapy.geoportal.gov.pl {
        //           header_up Host mapy.geoportal.gov.pl
        //           header_up -Origin
        //           header_up -Referer
        //       }
        //   }
        //
        // `uri replace` zmienia tylko path — query string zostaje nietkniety.
        // `header_up -Origin/-Referer` usuwa headery zdradzajace ze request
        // pochodzi z browsera, zeby GUGIK nie zablokowal przez anti-CORS
        // policy po stronie serwera.
        const capsUrl = "/proxy/gugik-wmts-caps?service=WMTS&request=getCapabilities";

        // Cache w localStorage — GetCapabilities jest statyczne i raz
        // pobrane XML jest aktualne przez dni/tygodnie. GUGIK potrafi
        // zwracać ERR_EMPTY_RESPONSE (serwer akceptuje TCP, zamyka bez
        // body) przy obciążeniu — cache pozwala mapie zadziałać mimo
        // chwilowej awarii backendu.
        const CAPS_CACHE_KEY = "wmtsCaps.gugik.orto.standard";
        const CAPS_TTL_MS = 24 * 60 * 60 * 1000; // 24 h
        const getCachedCaps = (): string | null => {
          try {
            const raw = localStorage.getItem(CAPS_CACHE_KEY);
            if (!raw) return null;
            const { xml, ts } = JSON.parse(raw);
            if (Date.now() - ts > CAPS_TTL_MS) return null;
            return xml;
          } catch { return null; }
        };
        const putCachedCaps = (xml: string) => {
          try {
            localStorage.setItem(CAPS_CACHE_KEY, JSON.stringify({ xml, ts: Date.now() }));
          } catch { /* quota exceeded — ignore */ }
        };

        // fetch z timeoutem i 3-krotnym retry z backoff 1s/2s/4s.
        // Timeout (8 s) jest krótszy niż domyślne 60+ s — szybciej
        // zrezygnujemy z zawieszonego TCP i pójdziemy na retry.
        const fetchCapsOnce = async (): Promise<string> => {
          const ctrl = new AbortController();
          const timer = setTimeout(() => ctrl.abort(), 8000);
          try {
            const resp = await fetch(capsUrl, { signal: ctrl.signal });
            if (!resp.ok) throw new Error(`GetCapabilities HTTP ${resp.status}`);
            return await resp.text();
          } finally {
            clearTimeout(timer);
          }
        };

        let capsXml: string | null = getCachedCaps();
        if (!capsXml) {
          let lastErr: unknown = null;
          for (let attempt = 1; attempt <= 3; attempt++) {
            try {
              capsXml = await fetchCapsOnce();
              putCachedCaps(capsXml);
              break;
            } catch (err) {
              lastErr = err;
              // eslint-disable-next-line no-console
              console.warn(`[OlMap] GetCapabilities proba ${attempt}/3 padla:`, (err as Error)?.message);
              if (attempt < 3) {
                await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, attempt - 1)));
              }
            }
          }
          if (!capsXml) throw lastErr ?? new Error("GetCapabilities failed");
        } else {
          // eslint-disable-next-line no-console
          console.log("[OlMap] GetCapabilities z cache localStorage (≤24h)");
        }

        const parser = new WMTSCapabilities();
        const parsedCaps = parser.read(capsXml);

        // Kolejność kandydatów matrixSet — zależna od CRS widoku mapy:
        //
        // Dla PL-1992 (EPSG:2180) — natywny EPSG:2180 jest szybki i bezstratny.
        //
        // Dla stref PL-2000 (EPSG:2176-2179) — reprojekcja jest nieunikniona
        // (GUGIK tileGrid dla stref jest niestabilny). Obserwacja z prod:
        // reprojekcja EPSG:2180 → EPSG:2178 w OpenLayers generuje 0 requestow
        // GetTile (OL akceptuje tileGrid source, ale nie emituje zadan —
        // prawdopodobnie przez duza roznice rzedow wielkosci wspolrzednych
        // miedzy 2180 ~[140k..880k] a 2178 ~[7.1M..7.9M]).
        // EPSG:3857 (WebMercator) jako source dziala stabilnie — OL ma
        // dobrze zoptymalizowana sciezke reprojekcji z Mercatora do
        // dowolnej projekcji. Koszt: kafle 3857 sa mniej gestej w obszarze
        // Polski niz 2180 (skalowanie Mercatora zmniejsza rozdzielczosc
        // przy naszej szerokosci ~52°N o ~1.6x), ale to wciaz WMTS cache
        // GUGIK, wiec TTFB < 300 ms.
        const candidateMatrixSets = mapCrs === CRS_PL1992
          ? ["EPSG:2180", "EPSG:3857"]
          : ["EPSG:3857", "EPSG:2180", mapCrs];
        const candidateLayers = ["ORTOFOTOMAPA", "Raster"];

        let options: any = null;
        let chosen: { layer: string; matrixSet: string } | null = null;
        outer: for (const layer of candidateLayers) {
          for (const matrixSet of candidateMatrixSets) {
            const opts = optionsFromCapabilities(parsedCaps, { layer, matrixSet });
            if (opts) {
              options = opts;
              chosen = { layer, matrixSet };
              break outer;
            }
          }
        }

        if (options && chosen) {
          // Defensive: GUGIK W XML zwraca dziś URL kafli z https://, ale
          // gdyby kiedyś wracał http:// — wymuszamy https na wszystkich
          // adresach (mixed content blokuje browser na stronach https).
          if (options.urls) {
            options.urls = options.urls.map((u: string) => u.replace(/^http:\/\//, "https://"));
          }
          if ((options as any).url) {
            (options as any).url = (options as any).url.replace(/^http:\/\//, "https://");
          }
          // eslint-disable-next-line no-console
          console.log("[OlMap] WMTS GUGIK załadowany z GetCapabilities — tile grid:", {
            origin: options.tileGrid?.getOrigin(0),
            resolutions: options.tileGrid?.getResolutions(),
            matrixSet: chosen.matrixSet,
            layer: chosen.layer,
            viewCrs: mapCrs,
            reprojection: chosen.matrixSet !== mapCrs,
          });
          orthoLayer = new TileLayer({
            // preload: 1 = pre-fetch kafle 1 zoom level wyżej i niżej.
            // WMTS GUGIK to statyczny cache (~10 KB jpeg, TTFB <300ms),
            // HTTP/2 multipleksuje preload-y w tle — pan/zoom feels instant.
            // Trade-off: ~2x więcej requestów per load, ale kafle są małe.
            preload: 1,
            source: new WMTS({
              ...options,
              // BEZ `crossOrigin` (mapy.geoportal.gov.pl nie zwraca CORS na
              // GetTile — kafle ladujemy jako zwykle <img> bez canvas-tainting
              // problem, bo natywne WMTS bez reprojekcji = bez canvasu).
              //
              // BEZ `tileLoadFunction` — celowo NIE uzywamy throttle dla
              // WMTS GUGIK. Kafle WMTS to statyczny cache po stronie GUGIK
              // (~10 KB jpeg, TTFB <300 ms) — browser HTTP/2 spokojnie
              // multipleksuje 50+ requestow rownolegle. Throttle (4/s) byl
              // zaprojektowany dla WMS GetMap (dynamiczne renderowanie,
              // GUGIK cierpi >5 req/s), nie dla WMTS. Lizmap tez nie
              // throttle-uje WMTS i ma swietna predkosc — robimy tak samo.
            }),
          });
        } else {
          // eslint-disable-next-line no-console
          console.warn(
            "[OlMap] optionsFromCapabilities nie znalazlo zadnej kombinacji layer × matrixSet; fallback na WMS",
          );
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[OlMap] WMTS GetCapabilities padło, fallback na WMS 1.1.1:", err);
      }

      if (!orthoLayer) {
        orthoLayer = buildOrthoWmsFallback();
      }

      // Działki ewidencyjne z WMS KIEG (Krajowa Integracja Ewidencji Gruntów
      // = router GUGIK do aktualnych danych powiatowych) — dwie warstwy, żeby
      // przy szerszym widoku mapa nie była zatłoczona konturami budynków i
      // numerami (czytelne z bliska):
      //
      // 1. `dzialki` — granice działek (czerwone linie), widoczne zawsze.
      // 2. `budynki` + `numery_dzialek` — od skali ~1:3000 (resolution < 0.8 m/px).
      //
      // Obie jako ImageWMS = JEDEN GetMap na widok, prosto z przeglądarki i bez
      // throttle — tak jak warstwy zewnętrzne („externalAccess”) w projektach
      // Lizmap 4GIS, gdzie te same WMS-y działają płynnie. Wcześniej były to
      // kafle 256 px TileWMS z limitem 5 req/s: 30+ kafli na widok = 6+ s
      // ładowania, a serwery powiatowe za KIEG i tak odpowiadały 429 przy
      // większej współbieżności. Jeden obraz na widok nie ma tego problemu
      // (limity dotyczą liczby żądań/s, nie rozmiaru obrazu). `ratio: 1.3` =
      // obraz nieco większy niż viewport, żeby małe przesunięcia nie odpytywały
      // serwera od nowa.
      //
      // Skala 1:3000 → resolution ~0.8 m/px (przy DPI 96, czyli 0.265 mm/px;
      // 0.265 mm × 3000 ≈ 0.8 m terenu na piksel ekranu). OL `maxResolution`
      // ukrywa warstwę gdy bieżący widok ma resolution > maxResolution
      // (czyli przy oddaleniu).
      const KIEG_WMS_URL = "https://integracja.gugik.gov.pl/cgi-bin/KrajowaIntegracjaEwidencjiGruntow";
      const buildKiegImageLayer = (layers: string, extra: Record<string, unknown> = {}) =>
        new ImageLayer({
          opacity: 0.9,
          ...extra,
          source: new ImageWMS({
            url: KIEG_WMS_URL,
            params: { LAYERS: layers, FORMAT: "image/png", TRANSPARENT: true, VERSION: WMS_VERSION },
            projection: mapCrs,
            ratio: 1.3,
            crossOrigin: "anonymous",
          }),
        });

      const dzialkiLayer = buildKiegImageLayer("dzialki");

      const budynkiNumeryLayer = buildKiegImageLayer("budynki,numery_dzialek", { maxResolution: 0.8 });

      const markerLayer = new VectorLayer({ source: markerSource, style: markerStyle });
      // parcelsLayer (wybrane działki / wnioski na mapie wniosków) — zIndex
      // ustalany kontekstowo:
      //  - default (mapy publiczne): 8 = nad wszystkimi builtin (max=7)
      //    dla wyrazistego oznaczenia user-zaznaczenia
      //  - selectedParcelsBelowCadastral=true (ApplicationsMapPage): 3 =
      //    pod KIEG granicami działek (5) ale nad ortofotomapą (1) i WMS-ami
      //    studium/mpzp (2-3); user widzi: ortofotomapa → wnioski → linie
      //    granic działek na wierzchu
      const parcelsLayerZIndex = selectedParcelsBelowCadastral ? 3 : 8;
      const parcelsLayer = new VectorLayer({
        source: parcelsSource,
        style: selectedStyle,
        zIndex: parcelsLayerZIndex,
      });

      // OpenStreetMap — warstwa opcjonalna, domyślnie wyłączona. Pod ortofoto
      // (jeśli user ją wyłączy, OSM zapewni orientację ulic/numerów budynków
      // z OSM community — komplementarnie do ortofoto i KIEG). Kafle 3857
      // są reprojektowane do widoku mapy (2180).
      const { default: OSM } = await import("ol/source/OSM");
      const osmLayer = new TileLayer({
        source: new OSM(),
        visible: layerVisibility?.osm ?? false,
      });

      // WMS GUGIK — Krajowa Integracja Miejscowych Planów Zagospodarowania
      // Przestrzennego (MPZP) i Krajowa Integracja Studium. Warstwy publiczne,
      // bez auth, WMS 1.1.1 (axis order nie rozjeżdża się między 2180/3857),
      // PNG z transparent=true, żeby ortofoto było widoczne tam, gdzie nie ma
      // planu. Integracje GUGIK KASKADUJĄ do serwerów gmin (często wolnych:
      // 1–8 s na GetMap), więc jak dla KIEG — ImageWMS, jeden obraz na widok,
      // zamiast 30 kafli, z których każdy czekałby na kaskadę osobno.
      //
      // Uwaga: KIMPZP publikuje `raster`, `wektor-*`, `granice`, `plany_granice`;
      // co gmina ma pod którą warstwą, zależy od tego, jak zgłosiła usługę
      // (np. Łącko zwraca rysunek starego planu pod `granice`, a `raster` ma
      // pusty). Gdy gmina ma własny geoportal, lepiej dodać jej WMS wprost
      // jako warstwę własną procedury (`defaultCustomWmsLayers`) i ukryć te
      // wbudowane (`hiddenBuiltinLayerIds`).
      const buildGugikIntegrationLayer = (url: string, layers: string, opacity: number, visible: boolean) =>
        new ImageLayer({
          opacity,
          visible,
          source: new ImageWMS({
            url,
            params: { LAYERS: layers, FORMAT: "image/png", TRANSPARENT: true, VERSION: WMS_VERSION },
            projection: mapCrs,
            ratio: 1.3,
          }),
        });
      const mpzpLayer = buildGugikIntegrationLayer(
        "https://mapy.geoportal.gov.pl/wss/ext/KrajowaIntegracjaMiejscowychPlanowZagospodarowaniaPrzestrzennego",
        "raster",
        layerOpacity?.mpzp ?? 0.7,
        layerVisibility?.mpzp ?? false,
      );
      // Usługa studium publikuje warstwy `gminy` i `studium` (NIE `raster` jak
      // KIMPZP) — z `raster` GUGIK odpowiadał LayerNotDefined i warstwa była
      // zawsze pusta.
      const studiumLayer = buildGugikIntegrationLayer(
        "https://mapy.geoportal.gov.pl/wss/ext/KrajowaIntegracjaStudiumKierunkowZagospodarowaniaPrzestrzennego",
        "studium",
        layerOpacity?.studium ?? 0.7,
        layerVisibility?.studium ?? false,
      );

      // Cieniowanie terenu — NMT GUGIK (ShadedRelief), pod ortofotomapą.
      // Warstwa powstaje od razu bez źródła; źródło dopinamy po
      // GetCapabilities (po utworzeniu mapy), żeby nie opóźniać jej startu.
      const hillshadeLayer = new TileLayer({
        preload: 1,
        visible: layerVisibility?.cieniowanie ?? false,
        opacity: layerOpacity?.cieniowanie ?? 1,
      });

      // Wymuś widoczność istniejących warstw wg props (jeśli podany).
      // Default: ortho=true, dzialki=true, budynki=true, plan=true.
      orthoLayer.setVisible(layerVisibility?.ortho ?? true);
      dzialkiLayer.setVisible(layerVisibility?.dzialki ?? true);
      budynkiNumeryLayer.setVisible(layerVisibility?.budynki ?? true);

      // Zachowaj refy — będziemy togglować widoczność przez useEffect zależny
      // od `layerVisibility`.
      layerRefs.current = {
        osm: osmLayer,
        ortho: orthoLayer,
        cieniowanie: hillshadeLayer,
        mpzp: mpzpLayer,
        studium: studiumLayer,
        dzialki: dzialkiLayer,
        budynki: budynkiNumeryLayer,
      };

      // Plan Ogólny — opcjonalna warstwa wektorowa. Jeśli rodzic przekazał
      // `planContext`, fetchujemy GeoJSON z backendu i renderujemy. Style
      // (kolory stref, kreskowania obszarów, etykiety oznaczeń) z
      // `lib/planStyleBuilder.ts` — wspólny kod z PlanPreviewMap, żeby
      // wygląd był identyczny w panelu admina i na publicznej mapie.
      //
      // declutter: true ukrywa kolidujące etykiety przy oddaleniu — bez
      // tego widok zoom-out byłby nieczytelny przy 1500+ poligonach.
      //
      // zIndex: warstwa plan jest POMIĘDZY działkami (1.5) a markerem (4),
      // żeby kolory stref były widoczne pod liniami granic działek
      // (mieszkaniec wybiera działkę widząc jaką ma strefę POG).
      const { buildPlanStyle } = await import("@/src/lib/planStyleBuilder");
      const planSource = new VectorSource();
      const planLayer = new VectorLayer({
        source: planSource,
        declutter: true,
        opacity: layerOpacity?.plan ?? 0.85,
        zIndex: 2,
        style: (feature: any, resolution: number) => {
          const p = feature.getProperties();
          // Filtr: obszary specjalne (POG/OUZ/OZS/OSD) widoczne tylko gdy
          // areasVisibleRef.current === true. StrefaPlanistyczna zawsze
          // widoczna (jeśli cała warstwa plan jest włączona).
          const isArea = p.featureType && p.featureType !== "StrefaPlanistyczna";
          if (isArea && !areasVisibleRef.current) {
            // Zwróć undefined = feature pominięty przy renderingu.
            return undefined;
          }
          return buildPlanStyle(p.symbol, p.oznaczenie, {
            showAreaLabels: showAreaLabelsRef.current,
            resolution,
          });
        },
      });
      planSourceRef.current = planSource;
      planLayerRef.current = planLayer;
      layerRefs.current.plan = planLayer;
      // Wymuś widoczność + opacity planu wg props (przed pierwszym renderem).
      planLayer.setVisible(layerVisibility?.plan ?? true);
      if (layerOpacity?.plan != null) planLayer.setOpacity(layerOpacity.plan);
      // Inicjalne wartości refów dla style callback (obszary + etykiety).
      areasVisibleRef.current = layerVisibility?.areas ?? true;
      showAreaLabelsRef.current = layerVisibility?.areaLabels ?? false;

      const center = fromLonLat([latLng.lng, latLng.lat], mapCrs);
      const { defaults: defaultInteractions } = await import("ol/interaction/defaults");

      const map = new OlMap({
        target: mapRef.current!,
        interactions: defaultInteractions({ doubleClickZoom: !immediateClick }),
        // Domyślnie 1 px — drgnięcie myszy/touchpada przy kliknięciu OL brał
        // za przeciąganie i klik w działkę nie dochodził.
        moveTolerance: 4,
        // Kolejność warstw od DOŁU (pierwsza w array = najniżej, renderowana
        // pod spodem) do GÓRY (rysowane na wierzchu):
        //   1. OSM — opcjonalna baza (domyślnie off)
        //   1a. cieniowanie terenu NMT GUGIK (domyślnie off)
        //   2. ortofotomapa GUGIK
        //   3. WMS MPZP GUGIK (opcjonalna, półprzezroczysta 0.7)
        //   4. WMS Studium GUGIK (opcjonalna, półprzezroczysta 0.7)
        //   5. KIEG granice działek (ImageWMS, linie po orto)
        //   6. Plan Ogólny POG (wektorowa, opacity 0.85)
        //   7. KIEG budynki + numery działek (ImageWMS, tekst nad strefami)
        //   8. Wybrane działki (wektorowa)
        //   9. Marker kliknięcia
        //
        // User może każdą warstwę wyłączyć/włączyć przez LayerControl.
        layers: [
          osmLayer,
          hillshadeLayer,
          orthoLayer,
          mpzpLayer,
          studiumLayer,
          dzialkiLayer,
          planLayer,
          budynkiNumeryLayer,
          parcelsLayer,
          markerLayer,
        ],
        view: new OlView({
          projection: mapCrs,
          center,
          zoom: initialZoom,
          minZoom: 0,
          // GUGIK ORTO StandardResolution przestaje serwować kafle na zoomie wyżej niż
          // ~19 (BBOX węższy niż ~150 m), dlatego z większego zbliżenia wracają
          // net::ERR_EMPTY_RESPONSE. Ograniczam widok do 19, co pokrywa rozdzielczość
          // ortofoto (~25 cm/piksel) i pozwala wygodnie zobaczyć granice działek.
          maxZoom: MAP_MAX_ZOOM,
        }),
      });

      // Pointermove → współrzędne w układzie mapy (x, y w metrach).
      // `evt.coordinate` jest już w projection widoku (mapCrs), bez konwersji.
      map.on("pointermove", (evt: any) => {
        if (evt.dragging) return;
        const [x, y] = evt.coordinate;
        if (Number.isFinite(x) && Number.isFinite(y)) {
          setCursorXY({ x, y, crs: mapCrs });
        }
      });

      // Licznik ładujących się kafli/obrazów (orto + dzialki + budynki).
      // Źródła kaflowe emitują tileloadstart/end/error, źródła ImageWMS
      // (budynki + numery) — imageloadstart/end/error. Sumujemy aktywne
      // żądania, żeby pokazać użytkownikowi „Ładowanie kafli…".
      // Flagi „raz-na-warstwę", żeby nie zalać konsoli logiem pierwszego
      // loaded/error kafla dla każdej warstwy osobno.
      const loggedFirstLoad = new WeakSet<any>();
      const loggedFirstError = new WeakSet<any>();
      const trackTileLoading = (source: any, label: string) => {
        const describe = (e: any) => e.tile?.src_ || e.tile?.getImage?.()?.src || e.image?.getImage?.()?.src;
        for (const kind of ["tile", "image"]) {
          source.on(`${kind}loadstart`, () => setTilesLoading((n) => n + 1));
          source.on(`${kind}loadend`, (e: any) => {
            setTilesLoading((n) => Math.max(0, n - 1));
            if (!loggedFirstLoad.has(source)) {
              loggedFirstLoad.add(source);
              // eslint-disable-next-line no-console
              console.log(`[OlMap] pierwszy kafel zaladowany — ${label}`, { src: describe(e) });
            }
          });
          source.on(`${kind}loaderror`, (e: any) => {
            setTilesLoading((n) => Math.max(0, n - 1));
            if (!loggedFirstError.has(source)) {
              loggedFirstError.add(source);
              // eslint-disable-next-line no-console
              console.warn(`[OlMap] pierwszy blad kafla — ${label}`, {
                src: describe(e),
                state: e.tile?.getState?.() ?? e.image?.getState?.(),
              });
            }
          });
        }
      };
      trackTileLoading(orthoLayer.getSource(), "ortho");
      trackTileLoading(dzialkiLayer.getSource(), "dzialki");
      trackTileLoading(budynkiNumeryLayer.getSource(), "budynki+numery");

      // Źródło cieniowania: WMTS z GetCapabilities (serwer zwraca
      // `Access-Control-Allow-Origin: *` — bez proxy Caddy). GUGIK publikuje
      // tylko matrixSet EPSG:2180; w widoku PL-2000 reprojekcja 2180→strefa
      // nie emituje GetTile (patrz ortofotomapa wyżej), więc tam od razu WMS.
      void (async () => {
        const HILLSHADE_BASE = "https://mapy.geoportal.gov.pl/wss/service/PZGIK/NMT/GRID1";
        let source: any = null;
        if (mapCrs === CRS_PL1992) {
          try {
            const xml = await fetchCapabilitiesCached(
              `${HILLSHADE_BASE}/WMTS/ShadedRelief?service=WMTS&request=GetCapabilities`,
              "wmtsCaps.gugik.nmt.shadedRelief",
            );
            const opts = optionsFromCapabilities(new WMTSCapabilities().read(xml), {
              layer: "Cieniowanie",
              matrixSet: "EPSG:2180",
            });
            if (opts) {
              // XML zwraca adresy kafli z http:// — mixed content na https.
              opts.urls = opts.urls?.map((u: string) => u.replace(/^http:\/\//, "https://"));
              source = new WMTS(opts);
            }
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn("[OlMap] WMTS cieniowania niedostępny, fallback na WMS:", err);
          }
        }
        if (cancelled) return;
        if (!source) {
          source = new TileWMS({
            url: `${HILLSHADE_BASE}/WMS/ShadedRelief`,
            params: { LAYERS: "Raster", FORMAT: "image/jpeg", VERSION: WMS_VERSION },
            projection: mapCrs,
            tileLoadFunction: createThrottledTileLoader(4, 250),
          });
        }
        hillshadeLayer.setSource(source);
        trackTileLoading(source, "cieniowanie");
      })();

      // Logika fetchULDK wyciągnięta z singleclick — może być wywołana
      // bezpośrednio (gdy parcelActionLabel undefined) albo przez popup
      // wyboru akcji po wyborze 'Wskaż działkę'.
      // `onInfo` — akcja dodatkowa (secondaryParcelAction): dostaje działkę
      // zamiast onParcelClick, bez pomijania działek już zaznaczonych.
      const performParcelClick = async (coord: [number, number], onInfo?: (info: ParcelInfo) => void) => {
        if (parcelLoadingRef.current) return;
        const lonLat = toLonLat(coord, mapCrs);
        const newLL = { lat: lonLat[1], lng: lonLat[0] };
        setLatLng(newLL);
        markerSource.clear();
        markerSource.addFeature(new Feature(new Point(coord)));
        if (!onParcelClick && !onInfo) return;
        parcelLoadingRef.current = true;
        setParcelLoading(true);
        setParcelMiss(false);
        try {
          const info = await fetchParcelByPoint(newLL.lat, newLL.lng);
          if (!info) setParcelMiss(true);
          if (onInfo) {
            if (info) onInfo(info);
            return;
          }
          if (
            !allowClickOnSelectedParcel &&
            info?.id &&
            selectedParcelsRef.current.some((p) => p.id === info.id)
          ) {
            // eslint-disable-next-line no-console
            console.log("[OlMap] dzialka", info.id, "juz dodana, pomijam");
            return;
          }
          onParcelClick?.(info);
        } finally {
          parcelLoadingRef.current = false;
          setParcelLoading(false);
        }
      };
      performParcelClickRef.current = performParcelClick;

      map.on(immediateClick ? "click" : "singleclick", async (evt: any) => {
        // Jeśli narzędzie pomiaru jest aktywne (Draw z flagą isMeasureTool)
        // — zignoruj kliknięcie, żeby nie wywoływać wyboru działki podczas
        // rysowania linii/poligonu pomiarowego.
        const interactions = mapInstance.current?.getInteractions?.().getArray?.() || [];
        const measureActive = interactions.some(
          (i: any) => i.get?.("isMeasureTool") && i.getActive?.(),
        );
        if (measureActive) return;

        // Zbierz features planu pod kliknięciem (strefa, obszary specjalne).
        let planHits: any[] = [];
        if (planLayerRef.current) {
          const { featureTypePriority } = await import("@/src/lib/planStyleBuilder");
          map.forEachFeatureAtPixel(
            evt.pixel,
            (f: any) => {
              const props = f.getProperties() as any;
              const { geometry, ...rest } = props;
              void geometry;
              planHits.push(rest);
              return false;
            },
            { layerFilter: (l: any) => l === planLayerRef.current },
          );
          planHits.sort(
            (a, b) => featureTypePriority(a.featureType) - featureTypePriority(b.featureType),
          );
        }

        // Tryb A: popup wyboru akcji (gdy parcelActionLabel jest zdefiniowany —
        // TenantPublicPage). User widzi przyciski 'Pokaż plan' / 'Wskaż działkę'
        // obok kursora, decyduje sam.
        if (parcelActionLabelRef.current) {
          setPendingChoice({
            pixel: [evt.pixel[0], evt.pixel[1]],
            coordinate: [evt.coordinate[0], evt.coordinate[1]],
            planHits,
          });
          return;
        }

        // Tryb B: bez popupu — stary flow, oba callbacki równolegle
        // (ApplicationsMapPage i inne miejsca bez parcelActionLabel).
        onPlanFeatureClickRef.current?.(planHits);
        await performParcelClick(evt.coordinate);
      });

      // Zamknij popup wyboru akcji gdy user zaczyna ruszać mapę — bo pixel
      // popup'a traci sens przy przesunięciu (popup jest positioned w DOM
      // w koordynatach pixel, nie śledzi za mapą).
      map.on("movestart", () => {
        setPendingChoice(null);
      });
      // Ręczna nawigacja użytkownika = widok „dotknięty” (patrz viewTouchedRef).
      map.on("pointerdrag", () => {
        viewTouchedRef.current = true;
      });
      map.getViewport().addEventListener("wheel", () => {
        viewTouchedRef.current = true;
      }, { passive: true });

      mapInstance.current = map;
      setReady(true);
      onMapReady?.(map);
    })();

    return () => {
      cancelled = true;
      if (mapInstance.current) {
        mapInstance.current.setTarget(undefined);
        mapInstance.current = null;
      }
    };
  }, []);

  // ─────────────────────────────────────────────────────────────────────
  // Plan Ogólny — fetch features gdy planContext jest podane / zmieni się.
  // Endpoint zwraca GeoJSON FeatureCollection w EPSG:2180 (po reprojekcji
  // backendowej z PL-2000). Dla widoku w 2180 — bez kolejnej reprojekcji.
  // Dla 2178/2179 — OL reprojektuje przez proj4.
  //
  // BEZ BBOX: dla planu jednego miasta (~1500-2000 features, 2-3 MB GeoJSON)
  // pobieramy wszystko raz. Jak plany urosną do 10000+, przejdziemy na
  // BBOX refresh w moveend (już gotowe po stronie API).
  // ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!ready || !planContext || !planSourceRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const url = `/tenant/${planContext.tenantId}/plan/${encodeURIComponent(planContext.procedureId)}/features?detail=preview`;
        const resp = await fetch(url);
        if (!resp.ok) {
          if (resp.status === 404) {
            // Plan nie istnieje dla tej procedury — to OK (procedura może
            // być pusta). Wyczyść warstwę i nie loguj jako błąd.
            planSourceRef.current.clear();
            return;
          }
          throw new Error(`HTTP ${resp.status}`);
        }
        const data = await resp.json();
        if (cancelled) return;
        const { default: GeoJSON } = await import("ol/format/GeoJSON");
        const format = new GeoJSON();
        const feats = format.readFeatures(data, {
          dataProjection: CRS_PL1992,
          featureProjection: mapCrsRef.current,
        });
        planSourceRef.current.clear();
        planSourceRef.current.addFeatures(feats);
        // eslint-disable-next-line no-console
        console.log(`[OlMap] plan loaded: ${feats.length} features`);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[OlMap] plan fetch failed:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [planContext?.tenantId, planContext?.procedureId, ready]);

  // Zoom do wybranego feature'a planu (np. z PlanSearchBox w TenantPublicPage).
  // Szukamy po ID w source warstwy plan, wywołujemy view.fit() na extencie.
  // maxZoom 18 — mała strefa nie musi wypełniać całej mapy.
  useEffect(() => {
    if (!ready || !mapInstance.current || !planSourceRef.current || planFocusFeatureId == null) return;
    const feature = planSourceRef.current.getFeatureById(planFocusFeatureId);
    if (!feature) return;
    const geom = feature.getGeometry();
    if (!geom) return;
    const extent = geom.getExtent();
    if (!extent || !Number.isFinite(extent[0])) return;
    mapInstance.current.getView().fit(extent, {
      padding: [80, 80, 80, 80],
      duration: 500,
      maxZoom: 18,
    });
  }, [planFocusFeatureId, ready]);

  // COG raster layers — rastrowe plany. Każdy URL = osobna warstwa WebGLTile.
  const cogLayersRef = useRef<any[]>([]);
  // Wersja licznik bumpowany gdy raster zostanie dodany do `layerRefs.current.raster`
  // — używana jako dependency w unified zIndex effect, żeby zaaplikował zIndex
  // z `layerOrder` na świeżo dodaną warstwę raster (bez tego raster ma default 0
  // i np. ortho lądowała nad nim).
  const [rasterRefsVersion, setRasterRefsVersion] = useState(0);
  useEffect(() => {
    if (!ready || !mapInstance.current) return;
    for (const layer of cogLayersRef.current) {
      mapInstance.current.removeLayer(layer);
    }
    cogLayersRef.current = [];
    if (!rasterCogUrls || rasterCogUrls.length === 0) {
      // Wyczyść referencję żeby unified effect nie próbował setZIndex na
      // już-usuniętym layerze.
      delete (layerRefs.current as any).raster;
      setRasterRefsVersion((v) => v + 1);
      return;
    }
    (async () => {
      try {
        const [{ default: GeoTIFF }, { default: WebGLTileLayer }] = await Promise.all([
          import("ol/source/GeoTIFF"),
          import("ol/layer/WebGLTile"),
        ]);
        for (let i = 0; i < rasterCogUrls.length; i++) {
          const source = new GeoTIFF({
            sources: [{ url: rasterCogUrls[i] }],
            convertToRGB: true,
            interpolate: true,
          });
          const layer = new WebGLTileLayer({
            source,
            opacity: layerOpacity?.raster ?? 0.85,
            zIndex: 1.5,
            visible: layerVisibility?.raster !== false,
          });
          cogLayersRef.current.push(layer);
          mapInstance.current!.addLayer(layer);
          // Dopasuj widok do zasięgu rastra po wczytaniu nagłówka COG — ale
          // tylko gdy użytkownik jeszcze nic nie zrobił z mapą i nie ma
          // wybranych działek. Raster dochodzi ~1 s po mapie; bez tego
          // warunku odbierał użytkownikowi wyszukaną/klikniętą działkę i
          // pokazywał cały projekt.
          if (i === 0) {
            source.getView().then((viewConfig: any) => {
              if (!viewConfig?.extent || !mapInstance.current) return;
              if (viewTouchedRef.current || selectedParcelsRef.current.length > 0) return;
              mapInstance.current.getView().fit(viewConfig.extent, {
                padding: [40, 40, 40, 40],
                duration: 500,
              });
            }).catch(() => {});
          }
        }
        if (cogLayersRef.current.length > 0) {
          layerRefs.current.raster = cogLayersRef.current[0];
          // Powiadom unified zIndex effect żeby ustawił zIndex na nowo
          // dodanym rasterze (inaczej raster ma default 0 i jest pod
          // warstwami które dostały zIndex z layerOrder przed jego mount).
          setRasterRefsVersion((v) => v + 1);
        }
      } catch (err) {
        console.error("Failed to load COG layers:", err);
      }
    })();
  }, [rasterCogUrls?.join(","), ready]);


  // Escape zamyka popup wyboru akcji.
  useEffect(() => {
    if (!pendingChoice) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPendingChoice(null);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [pendingChoice]);

  // Komunikat „nie znaleziono działki" znika po 3 s.
  useEffect(() => {
    if (!parcelMiss) return;
    const timer = setTimeout(() => setParcelMiss(false), 3000);
    return () => clearTimeout(timer);
  }, [parcelMiss]);

  // Toggle widoczności warstw — reagujemy na zmiany propsa `layerVisibility`.
  // Każda warstwa ma referencję w layerRefs.current; setVisible() tylko
  // przerenderowuje, nie pobiera kafli ponownie jeśli już są w cache OL.
  useEffect(() => {
    if (!ready || !layerVisibility) return;
    const refs = layerRefs.current;
    if (refs.osm) refs.osm.setVisible(layerVisibility.osm);
    if (refs.ortho) refs.ortho.setVisible(layerVisibility.ortho);
    if (refs.cieniowanie) refs.cieniowanie.setVisible(layerVisibility.cieniowanie);
    if (refs.mpzp) refs.mpzp.setVisible(layerVisibility.mpzp);
    if (refs.studium) refs.studium.setVisible(layerVisibility.studium);
    if (refs.dzialki) refs.dzialki.setVisible(layerVisibility.dzialki);
    if (refs.plan) refs.plan.setVisible(layerVisibility.plan);
    if (refs.budynki) refs.budynki.setVisible(layerVisibility.budynki);
    // Raster planu (COG) — wszystkie warstwy z cogLayersRef, nie tylko
    // pierwsza trzymana w refs.raster.
    for (const layer of cogLayersRef.current) layer.setVisible(layerVisibility.raster);
    // Etykiety obszarów + widoczność obszarów — ustaw refy i wywołaj
    // changed() na planLayer, żeby OL ponownie wywołał style callback.
    let planNeedsRerender = false;
    if (showAreaLabelsRef.current !== layerVisibility.areaLabels) {
      showAreaLabelsRef.current = layerVisibility.areaLabels;
      planNeedsRerender = true;
    }
    if (areasVisibleRef.current !== layerVisibility.areas) {
      areasVisibleRef.current = layerVisibility.areas;
      planNeedsRerender = true;
    }
    if (planNeedsRerender && refs.plan) refs.plan.changed();
  }, [layerVisibility, ready, rasterRefsVersion]);

  // Opacity WSZYSTKICH warstw z opacityKey. Osobny useEffect — stan w osobnym
  // obiekcie `layerOpacity` (w UI: inline slider pojawia się po kliknięciu
  // ikonki 🎚 obok checkboxa).
  useEffect(() => {
    if (!ready || !layerOpacity) return;
    const refs = layerRefs.current;
    if (refs.osm) refs.osm.setOpacity(layerOpacity.osm);
    if (refs.ortho) refs.ortho.setOpacity(layerOpacity.ortho);
    if (refs.cieniowanie) refs.cieniowanie.setOpacity(layerOpacity.cieniowanie);
    if (refs.mpzp) refs.mpzp.setOpacity(layerOpacity.mpzp);
    if (refs.studium) refs.studium.setOpacity(layerOpacity.studium);
    if (refs.dzialki) refs.dzialki.setOpacity(layerOpacity.dzialki);
    if (refs.plan) refs.plan.setOpacity(layerOpacity.plan);
    if (refs.budynki) refs.budynki.setOpacity(layerOpacity.budynki);
    // Raster planu (COG) był tu pominięty — suwak „Projekt MPZP" zmieniał
    // tylko zapisaną wartość, a mapa reagowała dopiero po odświeżeniu strony.
    // `rasterRefsVersion` w deps: raster dochodzi asynchronicznie po mapie,
    // więc świeżo dodana warstwa musi dostać bieżącą wartość suwaka.
    for (const layer of cogLayersRef.current) layer.setOpacity(layerOpacity.raster);
  }, [layerOpacity, ready, rasterRefsVersion]);

  // Custom WMS layers — diff dodanych/usunięć/zmian:
  //  - Każdy customWmsLayer z unikalnym id dostaje swój OL TileLayer
  //  - Zmiana `visible`/`opacity` aplikowana setVisible()/setOpacity()
  //  - Usunięcie z props → `map.removeLayer()` + usunięcie z refMap
  //  - Reorder w props (kolejność w array) → aktualizujemy zIndex
  //    tak, żeby pierwszy element = najniżej, ostatni = na górze (nad wbudowanymi)
  useEffect(() => {
    if (!ready || !mapInstance.current) return;
    (async () => {
      const refs = customWmsLayerRefs.current;
      const desired = customWmsLayers ?? [];
      const desiredIds = new Set(desired.map((l) => l.id));

      // Usuń warstwy których już nie ma w props.
      for (const [id, layer] of Array.from(refs.entries())) {
        if (!desiredIds.has(id)) {
          mapInstance.current.removeLayer(layer);
          refs.delete(id);
        }
      }

      if (desired.length === 0) return;

      const [
        { default: TileLayer },
        { default: ImageLayer },
        { default: ImageWMS },
        wmtsModule,
        { default: WMTSCapabilities },
      ] = await Promise.all([
        import("ol/layer/Tile"),
        import("ol/layer/Image"),
        import("ol/source/ImageWMS"),
        import("ol/source/WMTS"),
        import("ol/format/WMTSCapabilities"),
      ]);
      const WMTS = wmtsModule.default;
      const optionsFromCapabilities = (wmtsModule as any).optionsFromCapabilities;

      for (let idx = 0; idx < desired.length; idx++) {
        const def = desired[idx];
        let layer = refs.get(def.id);

        if (layer) {
          // Istniejąca — aktualizuj właściwości (zIndex zarządzany przez
          // unified reorder useEffect, nie przez idx dodawania).
          layer.setVisible(def.visible);
          layer.setOpacity(def.opacity);
          continue;
        }

        if (def.type === "wmts") {
          // WMTS — fetchujemy GetCapabilities i używamy `optionsFromCapabilities`
          // żeby OL sam zbudował tile grid / resolutions / matrix set pasujący
          // do widoku mapy. Idealnie chcemy EPSG:2180 (GUGIK natywnie je
          // obsługuje), fallback na cokolwiek co serwer ma.
          try {
            // URL może już mieć query (np. Lizmap: `…/lizmap/service?repository=x&project=y`)
            // — wtedy doklejamy przez `&`, nie drugi `?`.
            const capsUrl = `${def.url}${def.url.includes("?") ? "&" : "?"}service=WMTS&request=GetCapabilities`;
            const resp = await fetch(capsUrl);
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const xml = await resp.text();
            const capsParser = new WMTSCapabilities();
            const caps = capsParser.read(xml);
            // Wybór matrixSet: preferuj 2180, fallback na pierwszy
            const preferredMatrix = ["EPSG:2180", "EPSG:3857", "EPSG:4326"];
            let options: any = null;
            for (const ms of preferredMatrix) {
              options = optionsFromCapabilities(caps, { layer: def.layerName, matrixSet: ms });
              if (options) break;
            }
            if (!options) {
              // fallback bez matrixSet — OL wybierze pierwszy dostępny
              options = optionsFromCapabilities(caps, { layer: def.layerName });
            }
            if (!options) {
              throw new Error(`Nie znaleziono warstwy "${def.layerName}" w GetCapabilities`);
            }
            const wmtsSource = new WMTS(options);
            layer = new TileLayer({
              source: wmtsSource,
              visible: def.visible,
              opacity: def.opacity,
              // zIndex ustawi unified reorder useEffect poniżej.
            });
            mapInstance.current.addLayer(layer);
            refs.set(def.id, layer);
            // eslint-disable-next-line no-console
            console.log(
              `[customWms] dodano WMTS "${def.title}" layerName=${def.layerName} matrixSet=${options.matrixSet || "auto"}`,
            );
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error(
              `[customWms] WMTS FAIL "${def.title}": ${(err as Error).message}`,
            );
          }
          continue;
        }

        // WMS — ImageWMS zamiast TileWMS: bardziej niezawodny dla WMS-ów
        // nieoptymalizowanych pod tiling (typowe dla ArcGIS MapServer
        // rządowych). OL pobiera JEDEN obraz dla całego viewportu i robi
        // on-the-fly reprojekcję jeśli projekcja serwera różni się od widoku.
        const sourceCrs = pickWmsProjection(def.availableCrs, mapCrsRef.current);
        const wmsSource = new ImageWMS({
          url: def.url,
          params: {
            LAYERS: def.layerName,
            FORMAT: "image/png",
            TRANSPARENT: true,
            VERSION: "1.1.1",
          },
          projection: sourceCrs,
          ratio: 1,
        });
        wmsSource.on("imageloaderror", (evt: any) => {
          const src = evt.image?.getImage?.()?.src;
          // eslint-disable-next-line no-console
          console.warn(
            `[customWms] image load FAIL layer="${def.title}" layerName=${def.layerName} projection=${sourceCrs}`,
            { src, availableCrs: def.availableCrs },
          );
        });
        layer = new ImageLayer({
          source: wmsSource,
          visible: def.visible,
          opacity: def.opacity,
          // zIndex ustawi unified reorder useEffect poniżej (nie hardcode 10+idx
          // — to powodowało że custom zawsze lądował nad built-in niezależnie
          // od layerOrder).
        });
        // eslint-disable-next-line no-console
        console.log(
          `[customWms] dodano WMS "${def.title}" layerName=${def.layerName} projection=${sourceCrs} (availableCrs=${(def.availableCrs ?? []).join(",") || "unknown"})`,
        );
        mapInstance.current.addLayer(layer);
        refs.set(def.id, layer);
      }
    })();
  }, [customWmsLayers, ready]);

  // ─────── Unified zIndex management dla wszystkich warstw user-facing ───
  //
  // Jeden useEffect wyliczający effektywną kolejność warstw (z `layerOrder`
  // gdy user zmienił, lub sensowny default) i ustawiający `setZIndex` na
  // wszystkich built-in i custom w sposób IDEMPOTENTNY. Dzięki temu:
  //  - Toggle visibility built-in nie psuje stackingu (warstwa się pojawia
  //    z prawidłowym zIndex bez konieczności drag-drop).
  //  - Dodanie custom WMS nie kładzie go magicznie nad wszystko —
  //    respektuje zapisaną kolejność.
  //  - Usunięcie warstwy nie wymaga ręcznego przeliczania.
  //
  // Konwencja GIS: pierwsza pozycja w order = TOP mapy (najwyższy zIndex).
  // Default order: LAYERS const (top-level, bez areas/areaLabels) + custom
  // WMS w kolejności dodawania (najnowsze na dole listy).
  //
  // Wewnętrzne warstwy (parcelsLayer/markerLayer/plan-children) mają
  // statyczny wysoki zIndex z init — nie dotykamy ich.
  useEffect(() => {
    if (!ready || !mapInstance.current) return;
    const builtinRefs = layerRefs.current as Record<string, any>;
    const customRefs = customWmsLayerRefs.current;

    // Default built-in order z LAYERS const — plan na TOP, osm na BOTTOM.
    // (Object.keys(layerRefs) dałby kolejność insertion, co nie odpowiada
    // semantycznej hierarchii warstw — plan trafiłby na dno, osm na wierzch.)
    const defaultBuiltinOrder = BUILTIN_LAYERS
      .filter((l) => !l.parentOf && builtinRefs[l.id])
      .map((l) => l.id);

    // Zbuduj effektywną kolejność
    let order: string[];
    if (layerOrder && layerOrder.length > 0) {
      // User explicit — trust it. Brakujące built-in (np. nowa warstwa)
      // wstawiamy w ich domyślne miejsce, brakujące custom (dodany WMS zanim
      // user reorderował) — na koniec.
      const withBuiltins = insertMissingBuiltinLayers(layerOrder, defaultBuiltinOrder);
      const known = new Set(withBuiltins);
      const extras: string[] = [];
      for (const [id] of customRefs) {
        const key = `custom:${id}`;
        if (!known.has(key)) extras.push(key);
      }
      order = [...withBuiltins, ...extras];
    } else {
      // Default: built-in w logicznej kolejności z LAYERS const + custom
      // w kolejności wstawienia (najnowsze na dole listy = niższy zIndex).
      const customIds = Array.from(customRefs.keys()).map((id) => `custom:${id}`);
      order = [...defaultBuiltinOrder, ...customIds];
    }

    const maxIdx = order.length - 1;
    order.forEach((id, idx) => {
      const zIndex = maxIdx - idx;
      if (id.startsWith("custom:")) {
        const customId = id.slice("custom:".length);
        const layer = customRefs.get(customId);
        if (layer) layer.setZIndex(zIndex);
      } else {
        const layer = builtinRefs[id];
        if (layer) layer.setZIndex(zIndex);
      }
    });
  }, [layerOrder, ready, customWmsLayers, layerVisibility, rasterRefsVersion]);

  // Fly to new location
  useEffect(() => {
    if (!mapInstance.current || !ready) return;
    (async () => {
      const { fromLonLat } = await import("ol/proj");
      const { Feature } = await import("ol");
      const { Point } = await import("ol/geom");
      const center = fromLonLat([latLng.lng, latLng.lat], mapCrsRef.current);
      mapInstance.current.getView().animate({ center, duration: 500 });
      if (markerSourceRef.current) {
        markerSourceRef.current.clear();
        markerSourceRef.current.addFeature(new Feature(new Point(center)));
      }
    })();
  }, [latLng, ready]);
  // Dane procedury mogą dotrzeć po inicjalizacji OpenLayers. Reagujemy na
  // zmianę zoomu bez kosztownego tworzenia mapy od nowa.
  useEffect(() => {
    if (!mapInstance.current || !ready) return;
    const zoom = Number(initialZoom);
    if (!Number.isFinite(zoom)) return;
    mapInstance.current.getView().animate({
      zoom: clampZoom(zoom),
      duration: 500,
    });
  }, [initialZoom, ready]);


  // Synchronizuj ref z propsem — singleclick handler (zarejestrowany raz przy
  // mount mapy) używa ref-a, żeby uniknąć stale closure'a propsa.
  useEffect(() => {
    selectedParcelsRef.current = selectedParcelsGeometries
      ? selectedParcelsGeometries.map((p) => ({ id: p.id }))
      : [];
  }, [selectedParcelsGeometries]);

  // Update selected parcels
  useEffect(() => {
    if (!parcelsSourceRef.current || !ready) return;
    (async () => {
      const [{ default: GeoJSON }, { Style, Fill, Stroke }] = await Promise.all([
        import("ol/format/GeoJSON"),
        import("ol/style"),
      ]);
      parcelsSourceRef.current.clear();
      const currentIds = new Set((selectedParcelsGeometries ?? []).map((p) => p.id));
      const prevIds = prevParcelIdsRef.current;
      prevParcelIdsRef.current = currentIds;
      if (!selectedParcelsGeometries) return;
      const format = new GeoJSON();
      // Nowo dodane działki względem poprzedniego renderu. Dopasowujemy widok
      // gdy doszła dokładnie jedna (wyszukanie po numerze / kliknięcie) —
      // nie przy pierwszym hurtowym wczytaniu listy wielu działek (mapa
      // wniosków) i nie przy usuwaniu.
      const addedIds = prevIds ? [...currentIds].filter((id) => !prevIds.has(id)) : [...currentIds];
      const fitTargetId = addedIds.length === 1 ? addedIds[0] : null;
      let fitFeatures: any[] = [];

      // Helper: hex "#rrggbb" -> "rgba(r,g,b,alpha)". Tolerant.
      const hexToRgba = (hex: string, alpha: number): string => {
        const h = hex.replace("#", "").trim();
        const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
        const r = parseInt(full.substring(0, 2), 16);
        const g = parseInt(full.substring(2, 4), 16);
        const b = parseInt(full.substring(4, 6), 16);
        if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
          return `rgba(0,255,0,${alpha})`;
        }
        return `rgba(${r},${g},${b},${alpha})`;
      };

      for (const p of selectedParcelsGeometries) {
        if (!p.geometry) continue;
        try {
          const geom = Array.isArray(p.geometry) ? p.geometry[0] : p.geometry;
          const features = format.readFeatures(geom, {
            dataProjection: CRS_WGS84,
            featureProjection: mapCrsRef.current,
          });
          // Jeśli parcela ma własny kolor (np. z legendy symboli na Mapie
          // wniosków), ustawiamy indywidualny styl na każdym feature —
          // nadpisuje domyślny zielony styl layera.
          if (p.color) {
            const style = new Style({
              stroke: new Stroke({ color: p.color, width: 3 }),
              fill: new Fill({ color: hexToRgba(p.color, 0.3) }),
            });
            features.forEach((f: any) => f.setStyle(style));
          }
          parcelsSourceRef.current.addFeatures(features);
          if (p.id === fitTargetId) fitFeatures = features;
        } catch (err) {
          console.error("Failed to add parcel geometry:", err);
        }
      }
      // Przybliż do nowo wybranej działki. `cancelAnimations` przerywa
      // równoległą animację centrowania z efektu `latLng` (ten sam klik /
      // wyszukanie ustawia też latLng) — inaczej obie animacje walczyłyby
      // o środek widoku. maxZoom 17 ≈ 0.02 m/px w EPSG:2180 — górna granica,
      // żeby małe działki nie kończyły na ekstremalnym zbliżeniu.
      if (fitFeatures.length > 0 && mapInstance.current) {
        const { createEmpty, extend, isEmpty } = await import("ol/extent");
        const extent = createEmpty();
        fitFeatures.forEach((f: any) => extend(extent, f.getGeometry().getExtent()));
        if (!isEmpty(extent)) {
          const view = mapInstance.current.getView();
          view.cancelAnimations();
          view.fit(extent, { padding: [60, 60, 60, 60], maxZoom: 17, duration: 500 });
          viewTouchedRef.current = true;
        }
      }
    })();
  }, [selectedParcelsGeometries, ready]);

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      <div
        ref={mapRef}
        style={{
          width: "100%",
          height: "100%",
          // Cursor `wait` podczas fetch ULDK — wyrazny sygnal dla usera ze
          // klikniecie zostalo zarejestrowane i mapa "mysli", a nie zignorowano.
          cursor: parcelLoading ? "wait" : "",
        }}
      >
        {!ready && (
          <div className="flex items-center justify-center h-full bg-gray-100 text-gray-500">
            Ładowanie mapy...
          </div>
        )}
      </div>
      {/* Narzędzia pomiaru (odległość + powierzchnia) — prawy górny róg mapy.
          `ready` guard gwarantuje że mapInstance.current jest gotowy przed
          mountem MeasureTools. */}
      {ready && !hideMeasureTools && <MeasureTools map={mapInstance.current} />}
      {/* Hint dla użytkownika — jak korzystać z mapy. Overlay w górnej
          środkowej części. Ukryty gdy popup wyboru akcji jest aktywny. */}
      {ready && !pendingChoice && !hideClickHint && (
        <div
          style={{
            position: "absolute",
            top: 12,
            left: "50%",
            transform: "translateX(-50%)",
            padding: "6px 14px",
            background: "rgba(255,255,255,0.95)",
            border: "1px solid #d1d5db",
            borderRadius: 999,
            fontSize: 12,
            fontFamily: "system-ui, -apple-system, sans-serif",
            color: "#1f2937",
            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
            pointerEvents: "none",
            zIndex: 999,
            whiteSpace: "nowrap",
            maxWidth: "calc(100% - 24px)",
          }}>
          <span style={{ fontWeight: 600 }}>Kliknij na mapie</span>
          <span style={{ color: "#6b7280" }}>, aby wybrać akcję</span>
        </div>
      )}
      {/* Popup wyboru akcji przy kursorze — Tryb A (parcelActionLabel
          zdefiniowane, czyli TenantPublicPage z kontekstem procedury).
          Pozycjonowany w DOM pixelowo; znika gdy user ruszy mapę (movestart)
          albo wybierze opcję. Klawisz Escape też go zamyka. */}
      {ready && pendingChoice && (
        <div
          style={{
            position: "absolute",
            top: Math.max(8, pendingChoice.pixel[1] - 8),
            left: Math.min(
              Math.max(8, pendingChoice.pixel[0] + 10),
              // Nie wypuszczaj poza prawą krawędź: ograniczamy względem parent width.
              // (Popup ma max width 220px, margines 8.)
              typeof window !== "undefined" ? window.innerWidth - 228 : 10_000,
            ),
            minWidth: 200,
            maxWidth: 220,
            background: "#fff",
            border: "1px solid #d1d5db",
            borderRadius: 6,
            boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
            padding: 8,
            fontFamily: "system-ui, -apple-system, sans-serif",
            fontSize: 13,
            zIndex: 1100,
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}>
          <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 2 }}>
            Co chcesz zrobić w tym punkcie?
          </div>
          {pendingChoice.planHits.length > 0 && (
            <button
              type="button"
              onClick={() => {
                const hits = pendingChoice.planHits;
                setPendingChoice(null);
                onPlanFeatureClickRef.current?.(hits);
              }}
              style={{
                padding: "8px 10px",
                background: "#eff6ff",
                border: "1px solid #93c5fd",
                borderRadius: 4,
                color: "#1e40af",
                fontWeight: 600,
                cursor: "pointer",
                textAlign: "left",
                fontSize: 13,
              }}>
              ℹ Pokaż informacje o planie
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              const coord = pendingChoice.coordinate;
              setPendingChoice(null);
              performParcelClickRef.current?.(coord);
            }}
            style={{
              padding: "8px 10px",
              background: "#ecfdf5",
              border: "1px solid #6ee7b7",
              borderRadius: 4,
              color: "#065f46",
              fontWeight: 600,
              cursor: "pointer",
              textAlign: "left",
              fontSize: 13,
            }}>
            🎯 {parcelActionLabel}
          </button>
          {secondaryParcelAction && (
            <button
              type="button"
              onClick={() => {
                const coord = pendingChoice.coordinate;
                setPendingChoice(null);
                performParcelClickRef.current?.(coord, (info) =>
                  secondaryParcelActionRef.current?.onParcel(info),
                );
              }}
              style={{
                padding: "8px 10px",
                background: "#fff7ed",
                border: "1px solid #fdba74",
                borderRadius: 4,
                color: "#9a3412",
                fontWeight: 600,
                cursor: "pointer",
                textAlign: "left",
                fontSize: 13,
              }}>
              ⛰ {secondaryParcelAction.label}
            </button>
          )}
          <button
            type="button"
            onClick={() => setPendingChoice(null)}
            style={{
              padding: "4px 6px",
              background: "transparent",
              border: "none",
              color: "#6b7280",
              cursor: "pointer",
              textAlign: "right",
              fontSize: 11,
            }}>
            Anuluj (Esc)
          </button>
        </div>
      )}
      {/* Overlay z informacja "Pobieranie..." podczas fetchu ULDK. Centrowany
          na mapie, wpolprzezroczyste tlo. pointer-events: none zeby nie
          blokowac kliknieciem (drugi click i tak jest blokowany przez
          parcelLoadingRef w handlerze). */}
      {ready && parcelLoading && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            padding: "8px 16px",
            background: "rgba(255,255,255,0.92)",
            border: "1px solid #d1d5db",
            borderRadius: "6px",
            fontSize: "13px",
            fontFamily: "system-ui, -apple-system, sans-serif",
            color: "#1f2937",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            pointerEvents: "none",
            zIndex: 1000,
          }}
        >
          ⏳ Pobieranie informacji o działce…
        </div>
      )}
      {ready && parcelMiss && !parcelLoading && (
        <div
          role="status"
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            padding: "8px 16px",
            background: "rgba(254,242,242,0.95)",
            border: "1px solid #fca5a5",
            borderRadius: "6px",
            fontSize: "13px",
            fontFamily: "system-ui, -apple-system, sans-serif",
            color: "#991b1b",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            pointerEvents: "none",
            zIndex: 1000,
          }}
        >
          Nie udało się pobrać działki w tym miejscu — kliknij ponownie.
        </div>
      )}
      {/* Pasek statusu — współrzędne kursora + wskaźnik ładowania kafli.
          Absolutnie pozycjonowany przy dolnej krawędzi mapy, przezroczyste tło. */}
      {ready && (
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 10px",
            fontSize: "12px",
            fontFamily: "monospace",
            background: "rgba(255,255,255,0.85)",
            borderTop: "1px solid #e5e7eb",
            color: "#374151",
            pointerEvents: "none",
          }}>
          <span>
            {cursorXY
              ? `${cursorXY.crs}  x: ${cursorXY.x.toFixed(2)} m  y: ${cursorXY.y.toFixed(2)} m`
              : "x: —  y: —"}
          </span>
          <span>
            {parcelLoading
              ? "⏳ Pobieranie działki…"
              : tilesLoading > 0
              ? `⏳ Ładowanie kafli (${tilesLoading})…`
              : "✓ Mapa gotowa"}
          </span>
        </div>
      )}
    </div>
  );
}

export default OlMap;
export { fetchParcelByPoint };
